import {
  SUPPORTED_DOCUMENT_TYPES,
  SUPPORTED_IMAGE_TYPES,
  SUPPORTED_VIDEO_TYPES,
  SUPPORTED_AUDIO_TYPES,
} from '@packages/shared/constants/Assets';
import {INFINITY} from '@packages/domains/subscription-and-payments/plans/config';

export {PREBUILT_LAYOUTS_PLUGIN_ID} from './pluginIds';

/**
 * All available library primitive export types
 */
export const LIBRARY_EXPORT_TYPES = Object.freeze({
  LAYOUT: 'Layout' as const,
  COMPONENT: 'Component' as const,
  VARIABLE: 'Variable' as const,
  ASSET: 'Asset' as const,
  TAG_STYLE: 'TagStyle' as const,
  FONT: 'Font' as const,
  SOURCE_FILE: 'SourceFile' as const,
});

export enum LIBRARY_EXPORT_SUBTYPES {
  COMPONENT = 'COMPONENT',
  CODE_COMPONENT = 'CODE_COMPONENT',
  SKILL = 'SKILL',
  RULE = 'RULE',
}

/**
 * There are two possible sources for a shared Library:
 * - Workspace (shared privately to the Workspace)
 * - Marketplace (shared publicly to the Marketplace)
 */
export enum LIBRARY_SOURCE {
  WORKSPACE = 'WORKSPACE',
  MARKETPLACE = 'MARKETPLACE',
}

/**
 * Library Traits
 */
export enum LIBRARY_TRAITS {
  THEMEABLE = 'THEMEABLE',
  AI_SITE_GEN = 'AI_SITE_GEN',
}

/**
 * There are two possible statuses for a Library:
 * - Shared (if Library Profile status is ACTIVE)
 * - Unshared (if Library Profile status has any other value)
 */
export enum LIBRARY_PROFILE_STATUS {
  SHARED = 'SHARED',
  UNSHARED = 'UNSHARED',
}

export enum LIBRARY_STATUS {
  installed = 'installed',
  install_available = 'install_available',
  upgrade_available = 'upgrade_available',

  installing = 'installing',
  uninstalling = 'uninstalling',
}

/**
 * Used for analytics events
 */
export enum LIBRARY_ACTION {
  SHARE = 'share',
  UNSHARE = 'unshare',
  INSTALL = 'install',
  UNINSTALL = 'uninstall',
  PUSH_UPDATE = 'push update',
  PULL_UPDATE = 'pull update',
}

export enum LIBRARY_ACTION_SOURCE {
  DESIGNER = 'designer',
  DASHBOARD = 'dashboard',
  CLI = 'cli', // For code libraries only
  API = 'api', // Public REST API and MCP
}

export enum LIBRARY_TYPE {
  WEBFLOW = 'webflow', // Regular shared libraries
  CODE = 'code', // Code libraries
}

export const WorkspaceLibraryEntitlementConfigs = Object.freeze({
  ACCESS_WITH_NO_LIMIT: {
    hasAccess: true,
    entitlementLimit: INFINITY,
    currentUsage: 0,
  } as const,
  ENTITLEMENT_NOT_MET: {
    hasAccess: true,
    entitlementLimit: 1,
    currentUsage: 0,
  } as const,
  ENTITLEMENT_MET: {
    hasAccess: false,
    entitlementLimit: 1,
    currentUsage: 1,
  } as const,
  NO_ENTITLEMENT: undefined,
});

export const CURRENT_SITE_IDENTIFIER = 'CURRENT_SITE';

export const INSTALLED_LIBRARIES_BLOCK_SHARE_REASON = 'INSTALLED_LIBRARIES';

export const getSupportedAssetTypes = (): Set<string> => {
  const supportedTypes = [
    ...Object.keys(SUPPORTED_IMAGE_TYPES),
    ...Object.keys(SUPPORTED_DOCUMENT_TYPES),
    ...Object.keys(SUPPORTED_VIDEO_TYPES),
    ...Object.keys(SUPPORTED_AUDIO_TYPES),
  ];

  return new Set(supportedTypes);
};

export const LIBRARY_COMPILER_VERSIONS = Object.freeze({
  LATEST: 2,
  BG_VIDEO_AND_SEARCH_SUPPORT: 2, // Expanded Library Element Support (SCALE-1131)
  WITH_ASSETS: 1, // new assets compiler
  DEFAULT: 0,
});

export const MAX_ASSET_COUNT = 2000;
